import { Box, Dialog, DialogContent, IconButton, Alert ,Grid, CircularProgress } from '@mui/material';
import { BackgroundStyle, FlexContainer, SearchingBoxContainer, LongBlueButton, HeadingTitle } from "../../assets/style";
import { photoIcon } from '../../assets/imgs';
import { useEffect, useState } from "react";
import SearchDateTimePicker from "../../components/SearchDateTimePicker";
import dayjs from 'dayjs';
import MainContent from '../../components/MainContent';
import { recordDetailPageName } from "../../components/PageName";
import { useLazyGetPhotoListQuery } from "../../features/photo/photoSlice";
import SearchDropDownList from "../../components/SearchDropDownList";
import SearchInput from "../../components/SearchInput";
import RecordTableV2, { TableRow, TableHeader } from "../../components/RecordTableV2";
import { RecordModel } from "../../models/RecordModel";
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { logOut } from '../../features/auth/authSlice';
import { areaDropdown, districtDropdown, processFilter, processDateFilter, validateDates } from './helpers'
import PhotoSphereViewerComponent from '../../components/PhotoSphereViewerComponent';


const RecordDetail = () => {
    const [searchParams] = useSearchParams();
    const [getPhotoList, photoList] = useLazyGetPhotoListQuery();
    const [tableRows, setTableRows] = useState<any[]>([]);
    const [fromDate, setFromDate] = useState(dayjs().startOf('day').add(5, 'hour').add(30, 'minute'));
    const [toDate, setToDate] = useState(dayjs().startOf('day').add(18, 'hour').add(30, 'minute'));
    const [stationName, setStationName] = useState<string | null>(searchParams.get('stationName') ? searchParams.get('stationName') : "");
    const [district, setDistrict] = useState<string | null>(searchParams.get('district') ? searchParams.get('district') : "ALL");
    const [area, setArea] = useState<string | null>(searchParams.get('area') ? searchParams.get('area') : "ALL");
    const [data, setData] = useState<RecordModel<any>>();
    const [selectedStation, setSelectedStation] = useState<string>('');
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isImageOpen, setIsImageOpen] = useState(false);
    const [selectedPerspectiveImage, setSelectedPerspectiveImage] = useState<string[] | null>(null);
    const [isPerspectiveImageOpen, setIsPerspectiveImageOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const columns: TableHeader[] = [
        { field: 'stationName', headerName: t("Location"), width: 300, isSortable: false },
        { field: 'uploadDate', headerName: t("Date"), width: 200, isSortable: true },
        { field: 'time', headerName: t("Time"), width: 150, isSortable: false },
        {
            field: "thumbnailUrl",
            headerName: t("360° Photo"),
            width: 150,
            renderCell: (params: any) => (
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: 2,
                            transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePhotoClick(params);
                    }}
                >
                    <img
                        src={`${params.value}?token=${localStorage.getItem('accessToken')}`}
                        alt="Thumbnail"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </Box>
            )
        },
        {
            field: "photoPerspectiveUrls",
            headerName: t("Perspective Photo"),
            width: 150,
            renderCell: (params: any) => (
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePerspectivePhotoClick(params.value);
                    }}
                >
                    <img src={photoIcon} alt='logo' />
                </Box>
            )
        },
    ];


    const fetchDataForPage = async (pageNumber: any) => {
        // Fetch data for the specified page from the backend
        const data = await getPhotoList(
            {
                stationName: stationName ? stationName : null,
                fromDate: fromDate ? processDateFilter(fromDate) : null,
                toDate: toDate ? processDateFilter(toDate) : null,
                district: district ? processFilter(district) : null,
                area: area ? processFilter(area) : null,
                page: pageNumber,
                sortBy: 'uploadDate',
                sortDirection: 'asc'
            }
        );

        if (data.status === 'fulfilled') {
            return data.data.records.map((item: any) => ({
                id: item?.id,
                stationId: item.stationId,
                stationName: item.stationName,
                district: item.district,
                area: item.area,
                photoUrl: item.photoUrl,
                photoPerspectiveUrls: item.photoPerspectiveUrls,
                uploadDate: new Date(item.uploadDate).toLocaleDateString('en-US'),
                time: new Date(item.uploadDate).toLocaleTimeString('en-US'),
                thumbnailUrl: item.thumbnailUrl,
            }))
        }

        if (data.status === 'rejected') {
            alert(t('An error occurred. Please login again.'));
            dispatch(logOut());
            navigate('/');
        }

        // return [];
    };

    const handleAreaOnChange = (name: string) => {
        setArea(name);
    }

    const handleDistrictOnChange = (name: string) => {
        setDistrict(name);
    }

    const handleClosePhoto = () => {
        setIsImageOpen(false);
        setSelectedImage(null);
    };

    const handleClosePerspectivePhoto = () => {
        setIsPerspectiveImageOpen(false);
        setSelectedPerspectiveImage(null);
    };

    const handlePhotoClick = (rowData: any) => {
        //console.log("Button clicked for row:", rowData.row);
        // Add your custom logic here
        setSelectedStation(rowData.row.stationName);
        setSelectedImage(rowData.row.photoUrl);
        setIsImageOpen(true);
    };

    const handlePerspectivePhotoClick = (imageUrl: string[]) => {
        //console.log("handlePerspectivePhotoClick:", imageUrl);
        // Add your custom logic here
        setSelectedPerspectiveImage(imageUrl);
        setIsPerspectiveImageOpen(true);
    };

    const handleRowClick = () => {
        // console.log('row click');
    }

    const handleLatestClick = () => {
        navigate('/recordDetail/latest')
    }

    const handleResetClick = async () => {
        setFromDate(dayjs(null));
        setToDate(dayjs(null));
        setStationName('');
        setDistrict("ALL");
        setArea("ALL");
    }

    const handleSearchClick = async () => {
        //console.log('handleSearchClick fromDate', fromDate);
        //console.log('handleSearchClick toDate', toDate);
        setLoading(true);
        validateDates(fromDate, toDate, setError);
        try {
            getPhotoList({
                stationName: stationName ? stationName : null,
                fromDate: fromDate ? processDateFilter(fromDate) : null,
                toDate: toDate ? processDateFilter(toDate) : null,
                district: district ? processFilter(district) : null,
                area: area ? processFilter(area) : null,
                sortBy: 'uploadDate',
                sortDirection: 'asc'
            }).then(
                (res) => {
                    if (res.status === 'fulfilled') {
                        setLoading(false);
                        const tableRows = res?.data.records.map((item: any) => ({
                            id: item?.id,
                            stationId: item.stationId,
                            stationName: item.stationName,
                            district: item.district,
                            area: item.area,
                            photoUrl: item.photoUrl,
                            photoPerspectiveUrls: item.photoPerspectiveUrls,
                            uploadDate: new Date(item.uploadDate).toLocaleDateString('en-US'),
                            time: new Date(item.uploadDate).toLocaleTimeString('en-US'),
                            thumbnailUrl: item.thumbnailUrl,
                        }))
                        setTableRows(tableRows)
                        setData(res?.data)
                    }

                    if (res.status === 'rejected') {
                        setLoading(false);
                        alert(t('An error occurred. Please login again.'));
                        dispatch(logOut());
                        navigate('/');
                    }
                }
            )
        } catch (error) {

        }
    }
    useEffect(() => {
        // console.log("data", data)
    }, [data])

    useEffect(() => {
        handleSearchClick();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <MainContent type={recordDetailPageName}>
            <Box sx={{ bgcolor: '#456296' }}>
                <HeadingTitle>{t("Record Detail")}</HeadingTitle>
            </Box>
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
            <BackgroundStyle sx={{ zIndex: 0 }}>
                {/* 360 Photo Popup */}
                <Dialog
                    open={isImageOpen}
                    onClose={handleClosePhoto}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ padding: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={handleClosePhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            >
                            </IconButton>
                            {/* <img
                                src={selectedImage || ''}
                                alt="Full preview"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '80vh',
                                    borderRadius: 8
                                }}
                            /> */}

                            {selectedImage && <PhotoSphereViewerComponent stationName={selectedStation} image={selectedImage} title='360 Viewer' />}
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Perspective Photo Popup */}
                <Dialog
                    open={isPerspectiveImageOpen}
                    onClose={handleClosePerspectivePhoto}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ padding: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={handleClosePerspectivePhoto}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            >
                            </IconButton>
                            <Grid container spacing={0}>
                                {selectedPerspectiveImage && selectedPerspectiveImage.map((img,index) => (
                                    <Grid key={index} item xs={6}>
                                        <img
                                            src={`${img}?token=${localStorage.getItem('accessToken')}` || ''}
                                            alt="Full preview"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '80vh',
                                                display: 'block', // Remove inline gaps
                                                verticalAlign: 'top', // Fix baseline spacing
                                            }}
                                        />
                                    </Grid>
                                ))
                                }
                            </Grid>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Searching Filter */}
                <SearchingBoxContainer>
                    <FlexContainer>
                        <SearchInput title={t("Station Name")} value={stationName} setMethod={setStationName} />
                        <SearchDateTimePicker title={t("From Date")} value={fromDate} setMethod={setFromDate} />
                        <SearchDateTimePicker title={t("To Date")} value={toDate} setMethod={setToDate} />
                        <SearchDropDownList title={(t("Area"))} value={area} handleValueChange={(event: any) => handleAreaOnChange(event?.target?.value)} items={areaDropdown?.map((item: { fullName: any; }) => item.fullName)} />
                        <SearchDropDownList title={(t("District"))} value={district} handleValueChange={(event: any) => handleDistrictOnChange(event?.target?.value)} items={districtDropdown?.map((item: { fullName: any; }) => item.fullName)} />
                    </FlexContainer>
                    <FlexContainer>
                        <LongBlueButton onClick={handleSearchClick}>{t("Search")}</LongBlueButton>
                        <LongBlueButton onClick={handleResetClick}>{t("Reset")}</LongBlueButton>
                        <LongBlueButton onClick={handleLatestClick}>{t("Latest Photo Record")}</LongBlueButton>
                    </FlexContainer>
                </SearchingBoxContainer>

                {isLoading && <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300
                }}>
                    <CircularProgress size={60} />
                </Box>}
                {!isLoading && <RecordTableV2
                    columns={columns}
                    rows={tableRows}
                    checkbox={false}
                    pages={data?.totalPages}
                    totalCount={data?.totalCount}
                    onClick={handleRowClick}
                    updateTableRows={setTableRows}
                    fetchDataForPage={fetchDataForPage}
                ></RecordTableV2>
                }
            </BackgroundStyle>
        </MainContent >
    );
};
export default RecordDetail;